import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../modules/auth/useAuth";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  AvatarDropdown,
  Button,
  Ellipsis,
  EmDash,
  Modal,
  Nav,
  NavbarV2,
} from "@xfrontend/xui-react-plus";
import {
  Logout, GetInfo, Document, Settings, Translate, Account,
} from "@xfrontend/xicon";
import XLogo from "../../assets/XLogo.svg";
import { signOutClean, isGatewayIdp, setActiveLicenseId } from "../auth/idp";
import { useRbac } from "../auth/useRbac";
import { infraSetHeaderSize } from "../../infra/redux/actionCreator";
import VersionViewer from "./VersionViewer";
import OrgSwitcher from "./OrgSwitcher";
import { useLanguage, Language } from "#/i18n/LanguageContext";

const LANGUAGE_OPTIONS: ReadonlyArray<{ value: Language; label: string }> = [
  { value: "en", label: "English" },
  { value: "zh", label: "中文" },
  { value: "ja", label: "日本語" },
  { value: "th", label: "ภาษาไทย" },
];

const LangToggle = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  color: var(--x-gray-300, #cfd0d3);

  &:hover {
    color: var(--x-gray-200, #f6f6f7);
  }
`;

const LangMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 8px;
  min-width: 160px;
  padding: 8px 0;
  background: var(--x-purple-400, #6a6280);
  border-radius: 8px;
  z-index: 1050;
`;

const LangMenuItem = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 20px;
  border: none;
  background: ${(props) => (props.$active ? "rgba(255,255,255,0.1)" : "transparent")};
  color: var(--x-gray-200, #f6f6f7);
  font-size: 16px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const Header = (): JSX.Element => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("header");
  const { isSystemAdmin } = useRbac();
  const { language, setLanguage } = useLanguage();
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const closeLangMenu = useCallback((e: MouseEvent) => {
    if (langRef.current && !langRef.current.contains(e.target as Node)) {
      setLangMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (langMenuOpen) {
      document.addEventListener("mousedown", closeLangMenu);
      return () => document.removeEventListener("mousedown", closeLangMenu);
    }
    return undefined;
  }, [langMenuOpen, closeLangMenu]);

  const pathname = useMemo(
    () => location.pathname.split("/"),
    [location.pathname],
  );
  const [currentSelect, setCurrent] = useState<string[]>(pathname);
  const [showAbout, setShowAbout] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Debugging auth status
  useEffect(() => {
    if (auth.isAuthenticated) {
      console.log("Auth Success! Token:", auth.user?.access_token);
      console.log("User Profile:", auth.user?.profile);
    }
  }, [auth.isAuthenticated, auth.user]);

  useEffect(() => {
    if (pathname.includes("applications")) {
      setCurrent(["applications"]);
    } else if (pathname.includes("policies")) {
      setCurrent(["policies"]);
    } else if (pathname.includes("sandbox")) {
      setCurrent(["sandbox"]);
    } else if (pathname.includes("settings")) {
      setCurrent(["settings"]);
    } else if (pathname.includes("account")) {
      setCurrent(["account"]);
    } else {
      setCurrent(["dashboard"]);
    }
  }, [pathname]);

  useEffect(() => {
    dispatch(infraSetHeaderSize());
  }, []);

  // 跨 IdP 取使用者識別:Cognito id_token 有 email;Entra v2 id_token 預設無 email,
  // 改用 preferred_username(UPN,如 joseph.tu@cycraft.com),再退到顯示名 name。
  const profile = auth.user?.profile;
  const email = profile?.email ?? profile?.preferred_username ?? profile?.name;
  const menuIconStyle = { marginRight: 6 } as const;

  const renderDropdown = useMemo(
    () => (
      <div id="nav-userAction">
        <AvatarDropdown
          align="end"
          username={email ?? t("menu.fallbackUser")}
          id="nav-userAction"
        >
          <div style={{ padding: "0 16px", width: "200px" }}>
            <Ellipsis monospace={false}>{email ?? <EmDash />}</Ellipsis>
          </div>
          <AvatarDropdown.Divider />
          <AvatarDropdown.Item
            id="userAction-about"
            onClick={() => setShowAbout(true)}
          >
            <GetInfo className="icon" size={14} color="var(--bs-white)" style={menuIconStyle} />
            {t("menu.about")}
          </AvatarDropdown.Item>
          <AvatarDropdown.Item
            id="userAction-api-quick-guide"
            onClick={() => window.open("https://community.cycraft.ai/xecguard/doc", "_blank", "noopener,noreferrer")}
          >
            <Document className="icon" size={14} color="var(--bs-white)" style={menuIconStyle} />
            {t("menu.apiQuickGuide")}
          </AvatarDropdown.Item>
          <AvatarDropdown.Item
            id="userAction-settings"
            onClick={() => navigate("/settings")}
          >
            <Settings className="icon" size={14} color="var(--bs-white)" style={menuIconStyle} />
            {t("menu.settings")}
          </AvatarDropdown.Item>
          {isSystemAdmin && (
            <AvatarDropdown.Item
              id="userAction-management"
              onClick={() => navigate("/management")}
            >
              <Account className="icon" size={14} color="var(--bs-white)" style={menuIconStyle} />
              {t("menu.management")}
            </AvatarDropdown.Item>
          )}
          {isGatewayIdp && (
            <AvatarDropdown.Item
              id="userAction-switch-account"
              onClick={() => {
                // Switch account mid-session: drop the current account's license so the
                // next account's /me re-resolves, then re-auth with the account picker.
                // No logout needed — picking a different account replaces the session.
                setActiveLicenseId(null);
                auth.signinRedirect({ extraQueryParams: { prompt: "select_account" } });
              }}
            >
              <Account className="icon" size={14} color="var(--bs-white)" style={menuIconStyle} />
              {t("menu.switchAccount")}
            </AvatarDropdown.Item>
          )}
          <AvatarDropdown.Item
            id="userAction-logout"
            onClick={() => setShowLogoutConfirm(true)}
          >
            <Logout className="icon" size={14} color="var(--bs-white)" />
            {t("menu.logout")}
          </AvatarDropdown.Item>
        </AvatarDropdown>
      </div>
    ),
    [email, navigate, t, auth, isSystemAdmin],
  );

  const renderAbout = useMemo(
    () => showAbout && <VersionViewer show={showAbout} setShow={setShowAbout} />,
    [showAbout],
  );

  return (
    <div className="header">
      <NavbarV2 collapseOnSelect expand="lg">
        <NavbarV2.Brand href="/dashboard">
          <img
            src={XLogo}
            alt={t("logo.alt")}
            style={{ height: 28, marginRight: 24 }}
          />
        </NavbarV2.Brand>
        <OrgSwitcher />
        <NavbarV2.Toggle aria-controls="responsive-navbar-nav" />
        <NavbarV2.Collapse id="responsive-navbar-nav">
          <div className="me-auto navbar-nav">
            <Nav.Link
              id="nav-dashboard"
              as={NavLink}
              to="/dashboard"
              rel="noopner noreferrer"
              active={currentSelect.includes("dashboard")}
            >
              {t("nav.dashboard")}
            </Nav.Link>
            <Nav.Link
              id="nav-applications"
              as={NavLink}
              to="/applications"
              rel="noopner noreferrer"
              active={currentSelect.includes("applications")}
            >
              {t("nav.applications")}
            </Nav.Link>
            <Nav.Link
              id="nav-policies"
              as={NavLink}
              to="/policies"
              rel="noopner noreferrer"
              active={currentSelect.includes("policies")}
            >
              {t("nav.policies")}
            </Nav.Link>
            <Nav.Link
              id="nav-sandbox"
              as={NavLink}
              to="/sandbox"
              rel="noopner noreferrer"
              active={currentSelect.includes("sandbox")}
            >
              {t("nav.sandbox")}
            </Nav.Link>
          </div>
          <div className="d-flex align-items-center navbar-nav" style={{ gap: 12 }}>
            <div ref={langRef} style={{ position: "relative" }}>
              <LangToggle
                type="button"
                aria-label="Language"
                onClick={() => setLangMenuOpen((v) => !v)}
              >
                <Translate size={22} color="currentColor" />
              </LangToggle>
              {langMenuOpen && (
                <LangMenu>
                  {LANGUAGE_OPTIONS.map((opt) => (
                    <LangMenuItem
                      key={opt.value}
                      $active={opt.value === language}
                      onClick={() => { setLanguage(opt.value); setLangMenuOpen(false); }}
                    >
                      {opt.label}
                    </LangMenuItem>
                  ))}
                </LangMenu>
              )}
            </div>
            {renderDropdown}
          </div>
        </NavbarV2.Collapse>
      </NavbarV2>
      {renderAbout}
      <Modal
        title={t("logoutModal.title")}
        show={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        showCloseBtn
        restoreFocus={false}
      >
        <Modal.Body>
          {t("logoutModal.body")}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLogoutConfirm(false)}>
            {t("logoutModal.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowLogoutConfirm(false);
              signOutClean(() => auth.removeUser());
            }}
          >
            {t("logoutModal.confirm")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Header;
